import { useEffect, useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, ChevronLeft, ChevronRight, Download, Info, Tag, Search, Crop, Loader2, Users, Trash2 } from 'lucide-react'
import { RootState } from '@/store/store'
import { closeImageModal, navigateModal } from '@/store/slices/uiSlice'
import { useGetImageQuery, useLazySearchByImageQuery, useLazySearchByFaceQuery, useGetFacesForImageQuery, useSoftDeleteFacesMutation } from '@/store/api'
import type { FaceSummary } from '@/store/api'
import { startImageSearch, startFaceSearch, startRegionSelection, setSearchResults, setSearchError } from '@/store/slices/searchSlice'
import FaceOverlay from './FaceOverlay'
import FaceThumbnailStrip from './FaceThumbnailStrip'
import { previewUrl, signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

function ImageModal() {
  useSignedUrls()
  const dispatch = useDispatch()
  const modalImage = useSelector((state: RootState) => state.ui.modalImage)
  const modalImageList = useSelector((state: RootState) => state.ui.modalImageList)
  const { isSelectingRegion } = useSelector((state: RootState) => state.search)
  const { data: imageData, isLoading } = useGetImageQuery(modalImage!, { skip: !modalImage })

  // Touch/swipe state
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const [showControls, setShowControls] = useState(true)
  const [isFindingSimilar, setIsFindingSimilar] = useState(false)
  const [showFaces, setShowFaces] = useState(true)
  const [selectedFace, setSelectedFace] = useState<FaceSummary | null>(null)
  const selectedFaceRef = useRef<FaceSummary | null>(null)
  const [pendingDeleteFace, setPendingDeleteFace] = useState<FaceSummary | null>(null)
  const [isDeletingFace, setIsDeletingFace] = useState(false)

  // Track visible image area for face overlay alignment (object-contain compensation)
  const imgRef = useRef<HTMLImageElement>(null)
  const [imageRect, setImageRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null)

  const computeImageRect = useCallback(() => {
    const img = imgRef.current
    if (!img || !img.naturalWidth || !img.naturalHeight) {
      setImageRect(null)
      return
    }
    const naturalRatio = img.naturalWidth / img.naturalHeight
    const elementRatio = img.clientWidth / img.clientHeight
    let visibleWidth: number, visibleHeight: number, offsetX: number, offsetY: number

    if (naturalRatio > elementRatio) {
      // Wider than container — letterboxed top/bottom
      visibleWidth = img.clientWidth
      visibleHeight = img.clientWidth / naturalRatio
      offsetX = 0
      offsetY = (img.clientHeight - visibleHeight) / 2
    } else {
      // Taller than container — pillarboxed left/right
      visibleHeight = img.clientHeight
      visibleWidth = img.clientHeight * naturalRatio
      offsetX = (img.clientWidth - visibleWidth) / 2
      offsetY = 0
    }
    setImageRect({ left: offsetX, top: offsetY, width: visibleWidth, height: visibleHeight })
  }, [])

  // Keep ref in sync with state so handleFindSimilar always reads the latest value
  selectedFaceRef.current = selectedFace

  // Reset face selection and image rect when the displayed image changes
  useEffect(() => {
    setSelectedFace(null)
    setImageRect(null)
  }, [modalImage])

  // Recompute image rect on window resize so overlay stays aligned
  useEffect(() => {
    if (!modalImage) return
    window.addEventListener('resize', computeImageRect)
    return () => window.removeEventListener('resize', computeImageRect)
  }, [modalImage, computeImageRect])

  const [triggerSearchByImage] = useLazySearchByImageQuery()
  const [triggerSearchByFace] = useLazySearchByFaceQuery()
  const [softDeleteFaces] = useSoftDeleteFacesMutation()

  // Face data query - only fetch when image has face_ids
  const hasFaceIds = Boolean(imageData?.face_ids?.length)
  const { data: facesData } = useGetFacesForImageQuery(modalImage!, {
    skip: !modalImage || !hasFaceIds,
  })
  // Sort faces by bbox.x so thumbnail strip matches overlay left-to-right order
  const faces = [...(facesData?.faces ?? [])].sort((a, b) => a.bbox.x - b.bbox.x)

  // Find current index for navigation
  const currentIndex = modalImage ? modalImageList.indexOf(modalImage) : -1
  const hasNext = currentIndex >= 0 && currentIndex < modalImageList.length - 1
  const hasPrev = currentIndex > 0

  const handleClose = useCallback(() => {
    dispatch(closeImageModal())
  }, [dispatch])

  const handlePrev = useCallback(() => {
    dispatch(navigateModal('prev'))
  }, [dispatch])

  const handleNext = useCallback(() => {
    dispatch(navigateModal('next'))
  }, [dispatch])

  // Keyboard navigation
  useEffect(() => {
    if (!modalImage) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          handleClose()
          break
        case 'ArrowLeft':
          e.preventDefault()
          setSelectedFace(null)
          handlePrev()
          break
        case 'ArrowRight':
          e.preventDefault()
          setSelectedFace(null)
          handleNext()
          break
        case 'f':
          if (hasFaceIds) setShowFaces((prev) => !prev)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [modalImage, handleClose, handlePrev, handleNext, hasFaceIds])

  // Touch handlers for swipe navigation
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return

    const touchEndX = e.changedTouches[0].clientX
    const touchEndY = e.changedTouches[0].clientY
    const deltaX = touchEndX - touchStartX.current
    const deltaY = touchEndY - touchStartY.current

    // Only trigger if horizontal swipe is dominant and significant (> 50px)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0 && hasPrev) {
        handlePrev()
      } else if (deltaX < 0 && hasNext) {
        handleNext()
      }
    }

    touchStartX.current = null
    touchStartY.current = null
  }, [hasPrev, hasNext, handlePrev, handleNext])

  // Toggle controls on tap (mobile)
  const handleImageTap = useCallback(() => {
    setShowControls((prev) => !prev)
  }, [])

  // Find similar images/faces handler
  const handleFindSimilar = useCallback(async () => {
    if (!modalImage || isFindingSimilar) return

    // Read from ref to guarantee latest value (avoids stale closure)
    const face = selectedFaceRef.current

    setIsFindingSimilar(true)
    dispatch(closeImageModal())

    try {
      if (face) {
        // Face search: use ArcFace 512-D embedding on faces_v1
        dispatch(startFaceSearch({ faceId: face.face_id, cropUrl: signedSrc(face.crop_url) }))
        const results = await triggerSearchByFace({ faceId: face.face_id, limit: 20 }).unwrap()
        dispatch(setSearchResults(results))
      } else {
        // Image search: use Jina-CLIP 1024-D embedding on images_v1
        dispatch(startImageSearch(modalImage))
        const results = await triggerSearchByImage({ masterId: modalImage, limit: 20 }).unwrap()
        dispatch(setSearchResults(results))
      }
    } catch (error: unknown) {
      const detail =
        (error as { data?: { detail?: string } })?.data?.detail ??
        (error instanceof Error ? error.message : null)
      dispatch(setSearchError(detail || (face ? 'No similar faces found' : 'Search failed')))
    } finally {
      setIsFindingSimilar(false)
    }
  }, [modalImage, dispatch, triggerSearchByImage, triggerSearchByFace, isFindingSimilar])

  // Search by region handler
  const handleSearchRegion = useCallback(() => {
    if (!modalImage) return
    dispatch(startRegionSelection(modalImage))
  }, [modalImage, dispatch])

  // Delete face handler
  const handleDeleteFace = useCallback(async () => {
    if (!pendingDeleteFace || isDeletingFace) return
    setIsDeletingFace(true)
    try {
      await softDeleteFaces({ faceIds: [pendingDeleteFace.face_id] }).unwrap()
      if (selectedFace?.face_id === pendingDeleteFace.face_id) {
        setSelectedFace(null)
      }
      setPendingDeleteFace(null)
    } catch (error: unknown) {
      const detail =
        (error as { data?: { detail?: string } })?.data?.detail ??
        (error instanceof Error ? error.message : null)
      console.error('Failed to delete face:', detail || error)
    } finally {
      setIsDeletingFace(false)
    }
  }, [pendingDeleteFace, isDeletingFace, softDeleteFaces, selectedFace])

  if (!modalImage) return null

  const imgPreviewUrl = previewUrl(modalImage)

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={handleClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button - always visible, large touch target */}
      <button
        onClick={(e) => { e.stopPropagation(); handleClose() }}
        className={`absolute top-4 right-4 min-w-[44px] min-h-[44px] flex items-center justify-center text-white bg-black/50 hover:bg-black/70 rounded-full transition-all z-20 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'} md:opacity-100 md:pointer-events-auto md:bg-transparent md:hover:bg-white/10`}
        aria-label="Close"
      >
        <X size={28} />
      </button>

      {/* Navigation arrows - larger touch targets for mobile */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); handlePrev() }}
          className={`absolute left-2 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center text-white bg-black/50 hover:bg-black/70 rounded-full transition-all z-10 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'} md:opacity-100 md:pointer-events-auto md:left-4 md:bg-transparent md:hover:bg-white/10`}
          aria-label="Previous image"
        >
          <ChevronLeft size={32} />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); handleNext() }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center text-white bg-black/50 hover:bg-black/70 rounded-full transition-all z-10 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'} md:opacity-100 md:pointer-events-auto md:right-4 md:bg-transparent md:hover:bg-white/10`}
          aria-label="Next image"
        >
          <ChevronRight size={32} />
        </button>
      )}

      {/* Main content */}
      <div
        className="relative max-w-[90vw] max-h-[90vh] flex flex-col overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image - tap to toggle controls on mobile */}
        {isLoading ? (
          <div className="flex items-center justify-center w-96 h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
        ) : (
          <div className="relative inline-block self-center">
            <img
              ref={imgRef}
              src={imgPreviewUrl}
              alt={imageData?.caption || imageData?.file_name || 'Image'}
              className="max-w-full max-h-[65vh] object-contain rounded-lg select-none"
              onClick={handleImageTap}
              onLoad={computeImageRect}
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder.svg'
              }}
            />
            {showFaces && faces.length > 0 && (
              <FaceOverlay
                faces={faces}
                visible={showFaces}
                selectedFaceId={selectedFace?.face_id ?? null}
                onFaceClick={(face) =>
                  setSelectedFace((prev) => (prev?.face_id === face.face_id ? null : face))
                }
                imageRect={imageRect}
              />
            )}
          </div>
        )}

        {/* Bottom info bar - toggleable on mobile */}
        {imageData && (
          <div className={`mt-4 p-4 bg-black/60 rounded-lg text-white max-w-full transition-opacity ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'} md:opacity-100 md:pointer-events-auto`}>
            {/* Caption */}
            {imageData.caption && (
              <p className="text-sm mb-2 line-clamp-2">{imageData.caption}</p>
            )}

            {/* Metadata row */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-white/70">
              {/* File info */}
              <span className="flex items-center gap-1">
                <Info size={14} />
                {imageData.file_name || modalImage}
              </span>

              {/* Dimensions */}
              {imageData.width && imageData.height && (
                <span>{imageData.width} x {imageData.height}</span>
              )}

              {/* Classification */}
              <span className={`px-2 py-0.5 rounded ${imageData.classification?.is_photo ? 'bg-green-600/50' : 'bg-blue-600/50'}`}>
                {imageData.classification?.is_photo ? 'Photo' : imageData.classification?.is_screenshot ? 'Screenshot' : 'Document'}
              </span>

              {/* Face count */}
              {imageData.face_ids?.length > 0 && (
                <span>{imageData.face_ids.length} face{imageData.face_ids.length > 1 ? 's' : ''}</span>
              )}
            </div>

            {/* Face thumbnail strip */}
            {faces.length > 0 && (
              <FaceThumbnailStrip
                faces={faces}
                selectedFaceId={selectedFace?.face_id ?? null}
                onFaceSelect={(face) =>
                  setSelectedFace((prev) => (prev?.face_id === face.face_id ? null : face))
                }
                onFaceDelete={(face) => setPendingDeleteFace(face)}
              />
            )}

            {/* Tags */}
            {imageData.tags?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Tag size={14} className="text-white/50" />
                {imageData.tags.slice(0, 10).map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-white/10 rounded text-xs">
                    {tag}
                  </span>
                ))}
                {imageData.tags.length > 10 && (
                  <span className="text-xs text-white/50">+{imageData.tags.length - 10} more</span>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              {/* Find Similar Button */}
              <button
                onClick={(e) => { e.stopPropagation(); handleFindSimilar() }}
                disabled={isFindingSimilar}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/80 hover:bg-primary text-primary-foreground rounded text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isFindingSimilar ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : selectedFace ? (
                  <Users size={14} />
                ) : (
                  <Search size={14} />
                )}
                {selectedFace ? 'Find Similar Face' : 'Find Similar'}
              </button>

              {/* Delete Face Button (when face selected) */}
              {selectedFace && (
                <button
                  onClick={(e) => { e.stopPropagation(); setPendingDeleteFace(selectedFace) }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/80 hover:bg-red-600 text-white rounded text-sm font-medium transition-colors"
                >
                  <Trash2 size={14} />
                  Delete Face
                </button>
              )}

              {/* Search Region Button */}
              <button
                onClick={(e) => { e.stopPropagation(); handleSearchRegion() }}
                disabled={isSelectingRegion}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary/80 hover:bg-secondary text-foreground rounded text-sm transition-colors disabled:opacity-50"
              >
                <Crop size={14} />
                Search Region
              </button>

              {/* Faces Toggle Button */}
              {hasFaceIds && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowFaces((prev) => !prev) }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${
                    showFaces
                      ? 'bg-cyan-600/80 hover:bg-cyan-600 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white/70'
                  }`}
                >
                  <Users size={14} />
                  Faces
                </button>
              )}

              {/* Download Button */}
              <a
                href={imgPreviewUrl}
                download={imageData.file_name || `${modalImage}.png`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-sm transition-colors"
              >
                <Download size={14} />
                Download
              </a>
            </div>
          </div>
        )}

        {/* Image counter - toggleable on mobile */}
        {currentIndex >= 0 && modalImageList.length > 0 && (
          <div className={`absolute top-4 left-4 px-3 py-1 bg-black/60 rounded text-white/70 text-sm transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'} md:opacity-100`}>
            {currentIndex + 1} / {modalImageList.length}
          </div>
        )}
      </div>

      {/* Face delete confirmation dialog */}
      {pendingDeleteFace && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); setPendingDeleteFace(null) }}
        >
          <div
            className="bg-card border border-border rounded-xl p-6 max-w-sm mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden border border-border flex-shrink-0">
                <img
                  src={signedSrc(pendingDeleteFace.crop_url)}
                  alt="Face to delete"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Delete this face?</h3>
                <p className="text-xs text-muted-foreground">
                  The face will be hidden from results. You can restore it from the Faces page.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingDeleteFace(null)}
                className="px-4 py-1.5 text-sm rounded-lg bg-secondary hover:bg-secondary/80 text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteFace}
                disabled={isDeletingFace}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50"
              >
                {isDeletingFace ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageModal
